/**
 * The **data-plane destination registry**: eight task-shaped destinations, carried in the **PATH**.
 *
 * ## ⭐⭐ R9 **N-12**: THESE ARE ROUTES NOW, NOT A `?tab=` SEARCH PARAM
 *
 * Owner, 2026-08-07: *"by moving apps into the LHM like that we are now setting a precedent that we
 * need to follow for any top level choice like that, specifically tenants and data planes"* and
 * *"The tabs should become real routes, correct?"* — **yes.**
 *
 * ⛔ **THIS FILE PREVIOUSLY ARGUED THE OPPOSITE AT LENGTH, AND THE ARGUMENT WAS SOUND WHEN WRITTEN.**
 * It is retained in summary rather than deleted, because a reader who finds routes here and
 * remembers the old reasoning needs to know it was *overtaken*, not overlooked:
 *
 *  · **The old reason 1** — *"a link to a path with no manifest entry does not 404, it silently
 *    teleports the operator to the Tenants list"* — was true and is now **fixed at the source**
 *    rather than avoided: `AppHost` emits ONE splat route for a detail scope (`R9-14`), and an
 *    unknown trailing segment redirects to the record's own first destination (`R9-15`), never to
 *    the `*` arm. The hazard that made a search param safer no longer exists on this path.
 *  · **The old reason 2** — *"adding routes obliges `extract-routes`"* — is simply **false**, and
 *    measured so: `cloud/cp/test/api-coverage/extract-routes.ts` walks `*.controller.ts` and reads
 *    NestJS decorators. A client-side React Router path is invisible to it, so a UI route needs
 *    neither a run nor an `expected-routes.json` entry.
 *
 * ⇒ **What routes buy, and the fourth is the one that decided it (N-12):**
 *  1. `railAriaCurrent` resolves **path** matching, so a query-param destination could never report
 *     itself current without a special case — a rail item that can never highlight itself.
 *  2. App destinations are already routes; routes make the two scopes **symmetric**, which is the
 *     whole point of the N-11 precedent.
 *  3. Linkable by construction — this codebase's own argument (`ReachPane`): *"an answer you cannot
 *     link to is an answer you cannot hand to a colleague."*
 *  4. ⭐ **It collapses TWO parallel registries of destinations into ONE.** `DP_TABS` used to be a
 *     registry `manifest.ts` knew nothing about, so the rail would have been generated from the
 *     manifest for global entries and from a per-pane array for scoped ones. **`manifest.ts` now
 *     PROJECTS this file** (`DP_SCOPED_ENTRIES`) rather than restating it, so a seventh destination
 *     is one edit here and nothing else.
 *  5. `route-table.test.tsx` and the route census can **see** a route; a `?tab=` value is invisible
 *     to them.
 *
 * ## ⛔ WHY THIS FILE IS THE REGISTRY AND `manifest.ts` IS THE PROJECTION
 *
 * The static import direction is `shell/manifest.ts → panes/dp-tabs.ts`. That is the direction
 * `manifest.ts` already runs in — every entry's `loader` names a pane — and it is the one that keeps
 * `TabBody`'s exhaustive `switch` honest: the union has to be declared where the bodies are, or the
 * switch cannot be exhaustive over it. ⚠️ Deriving the union from the manifest instead was tried on
 * paper and rejected: `subPath` for Overview is `''`, so the switch arms would read `case '':`, which
 * is a worse thing to read than one static import. ⛔ **This module must never import from `shell/`**
 * — that closes a cycle.
 *
 * ## ⛔ THE DESTINATIONS ARE TASK-SHAPED, NOT ONE-PER-OLD-SECTION
 *
 * The pane's nine stacked sections did not become nine destinations. Nine drawers is the
 * noun-cabinet failure this console already carries elsewhere — a destination is only navigation if
 * it answers a question an operator actually arrives with:
 *
 *  · **Overview** — *is it healthy, what is it, who is on it?*
 *  · **Apps**: *which apps run on it?*
 *  · **History** — *what happened to it over time?*
 *  · **Logs** — *what is it saying?*
 *  · **Metrics** — *how is it performing?*
 *  · **Diagnostics** — *why is it broken?*
 *  · **Relocations**: *where does its blob data move?*
 *  · **Capacity** — *how big is it?*
 *
 * ⭐ **THE FIVE-DESTINATION CAP IS RETIRED, AND IT WAS RETIRED BY THE STRUCTURE, NOT WAIVED.** This
 * file used to carry a hard rule — *"Do not add a sixth without collapsing another: the strip wraps
 * rather than overflowing, so a sixth is a second row, not a hidden item."* That rule was a
 * statement about **`PaneTabStrip`'s layout**, not about human attention: a horizontal strip in a
 * fixed-width chrome slot has a real capacity, and Miller was the secondary argument.
 *
 * ⇒ These destinations are now items in a **vertical rail** that already carries 31 of them and
 * scrolls. There is no second row to wrap onto and nothing to hide. ⛔ So ruling **D-1** (Metrics as
 * the sixth) did not overrule the cap — **the conversion dissolved the constraint the cap was
 * protecting against**, and the conversion is what makes D-1 free. ⚠️ A ninth destination is still
 * a design question about *attention*; it is no longer a question about *pixels*.
 */

/**
 * ⛔ **DECLARATION ORDER IS RENDER ORDER, AND IT IS DELIBERATE** — Overview first because it answers
 * the question an operator arrives with; Diagnostics fifth because it is where they go once
 * something has said the DP is wrong; Capacity last because it is planning, not triage.
 *
 * ⭐ **`logs` sits THIRD, and the position is the argument for the change that created it.** The
 * owner asked for logs to be pulled out precisely because reaching them meant opening Diagnostics
 * and scrolling past metrics and traces. Putting them *after* Diagnostics would move the click
 * without shortening the path to the thing he actually reads first. The triage order an operator
 * follows is: *what is it* → *what happened to it* → *what is it saying* → *how is it performing* →
 * *why is it broken*.
 *
 * ## ⚠️ `metrics` IS FOURTH, AND RULING D-1 DID NOT SAY WHERE — this is the ground I chose on
 *
 * D-1 says only *"`DataPlaneMetricsScreen` moves into `dp-tabs.ts` alongside Overview / History /
 * Logs / Diagnostics / Capacity"*. Three independent facts put it at position four, and none of them
 * is a preference:
 *
 *  1. ⛔ **Appending it after Capacity is the one placement the file's own stated invariant
 *     forbids** — *"Capacity last because it is planning, not triage"*. Metrics is triage.
 *  2. **It is where its grid used to physically live.** `DataPlaneDiagnosticsTab.tsx` records the
 *     metrics grid's removal as *"THE METRICS DOOR — WHERE A GRID USED TO BE (V-1)"*. Metrics
 *     immediately before Diagnostics puts it back beside the panel it was carved out of.
 *  3. It completes the observability run — Logs then Metrics — before the *why is it broken* step.
 *
 * ⇒ Reversing this is a one-line edit to this array. It is flagged rather than buried because the
 * ruling left it open and a silent choice in a place the ruling did not reach is how a decision
 * stops being reviewable.
 */
export const DP_TABS = [
  'overview',
  /*
   * ⭐ G1 / M-12 — INDEX 1, and the position is MEASURED rather than preferred:
   *   1. The triage order above fixes *what is it* → *what happened to it* → *what is it saying* →
   *      *how is it performing* → *why is it broken*. "What is RUNNING on it" is the other half of
   *      *what is it*, so it sits beside Overview rather than after the diagnostic destinations.
   *   2. `dp-tabs.test.ts` asserts `indexOf('capacity') === DP_TABS.length - 1`, so APPENDING is
   *      forbidden — capacity is planning, not triage.
   *   3. `dp-tabs.test.ts` also asserts `history < logs < diagnostics`; inserting at 1 preserves it.
   *   4. ⭐ The OLD console put Apps in exactly this slot — `DpDetailPage.tsx:191` is the first
   *      non-index route under the DP — so an operator's muscle memory is preserved, not reset.
   */
  'apps',
  'history',
  'logs',
  'metrics',
  'diagnostics',
  /*
   * 2026-10-13-blob-buckets R-089 item 4: the blob relocation plans, before `capacity`, because
   * `dp-tabs.test.ts` keeps Capacity last. A relocation acts on where the data lives, next to the
   * planning destination that edits the allowed locations.
   */
  'relocations',
  'capacity',
] as const;

export type DpTab = (typeof DP_TABS)[number];

/**
 * ⛔ The destination an operator lands on at the record's own URL, and the target of every
 * unrecognised trailing segment (**R9-15**). Overview, never "the last one you looked at": a URL is
 * a claim about what you will see, and honouring stored state over the path would make the same link
 * show two different screens to two operators.
 */
export const DP_TAB_DEFAULT: DpTab = 'overview';

export const DP_TAB_LABELS: Readonly<Record<DpTab, string>> = {
  overview: 'Overview',
  apps: 'Apps',
  history: 'History',
  logs: 'Logs',
  metrics: 'Metrics',
  diagnostics: 'Diagnostics',
  relocations: 'Relocations',
  capacity: 'Capacity',
};

/**
 * The lucide key each destination shows in the rail.
 *
 * ⛔ **Every value here must be a key of `NavRail`'s `ICONS` map**, and an unregistered key renders
 * `LayoutDashboard` with no error, no warning and no test failure. `manifest-icons.test.ts` is the
 * build-time gate; this comment is the reminder at the point of edit.
 *
 * ⚠️ `Activity` is also `platform-in-flight`'s icon and `Gauge` is also `platform-capacity`'s. That
 * is fine and deliberate: those two are GLOBAL entries in a different rail group, and a glyph
 * repeated across groups reads as a shared *kind of thing* rather than as a collision.
 */
export const DP_TAB_ICONS: Readonly<Record<DpTab, string>> = {
  overview: 'LayoutDashboard',
  /*
   * ⛔ `Boxes` is ALREADY REGISTERED in `NavRail`'s `ICON_SOURCE` as `app-fixtures`' glyph, so this
   * destination needs NO `NavRail` change. That is accuracy rather than convenience: this screen
   * renders per-FIXTURE status buckets, which is the same kind of thing `app-fixtures` names — and
   * the repetition-across-groups allowance above covers it.
   */
  apps: 'Boxes',
  history: 'Activity',
  logs: 'ScrollText',
  metrics: 'LineChart',
  diagnostics: 'Stethoscope',
  /*
   * `Workflow` is already in `NavRail`'s `ICON_SOURCE` (`platform-channels`, `releases-launch-graph`):
   * a graph of things that flow between stages, which a plan of eight steps is. No `NavRail` change.
   */
  relocations: 'Workflow',
  capacity: 'Gauge',
};

export function isDpTab(value: string): value is DpTab {
  return (DP_TABS as readonly string[]).includes(value);
}

/**
 * The path segment for a destination, relative to the data plane's own URL.
 *
 * ⛔ **`DP_TAB_DEFAULT` IS THE EMPTY SEGMENT — the record's URL *is* Overview**, exactly as
 * `app-overview` carries `subPath: ''` and `/apps/:appId` is the app's Overview. ⚠️ This is the one
 * place the route form deliberately *differs* from the retired search-param form, which returned
 * `?tab=overview` for the default on the stated grounds that *"one tab whose href is `''` makes the
 * comparison special-cased"*. That reasoning was about a strip comparing href STRINGS; a rail
 * compares PATHS through `NavLink`, which handles a parent path with `end` and needs no special
 * case. ⇒ Two URLs for one destination would be the defect here, not one URL for two things.
 *
 * ⇒ **Consequence, stated rather than discovered: `/platform/data-planes/:dpId/overview` is NOT a
 * URL.** It is an unrecognised segment and redirects to the record root under R9-15 — byte-for-byte
 * what `/apps/:appId/overview` already does (`AppScopeRoute`'s `visibleAppScoped.find(e => e.subPath
 * === remainder)` matches nothing and redirects to the app root).
 */
export function dpTabSegment(tab: DpTab): string {
  return tab === DP_TAB_DEFAULT ? '' : tab;
}

/** ⛔ The ONE place a data-plane destination URL is built. Basename-relative, like every path. */
export function dpTabPath(dpId: string, tab: DpTab): string {
  const segment = dpTabSegment(tab);
  return segment === '' ? `/platform/data-planes/${dpId}` : `/platform/data-planes/${dpId}/${segment}`;
}

/** The console route of the Data planes list: the target of a data-plane link whose id is refused. */
export const DATA_PLANES_ROUTE = '/platform/data-planes';

/**
 * The static child segment of the create screen under {@link DATA_PLANES_ROUTE}. The manifest's
 * data-plane `create.segment` reads it, so the route and the refused id are one value.
 */
export const DATA_PLANE_CREATE_SEGMENT = 'new';

/* The characters a data-plane id may hold in a path segment. An allowlist, so a new bad form fails closed. */
const SAFE_DP_ID = /^[A-Za-z0-9._~-]+$/;

/*
 * The ids the allowlist admits that name another route: the two dot segments and the create screen,
 * in lower case. The router matches a route path in any letter case, so the check lower-cases the id.
 */
const RESERVED_DP_IDS: ReadonlySet<string> = new Set(['.', '..', DATA_PLANE_CREATE_SEGMENT]);

/**
 * The route of one data plane's screen or one of its tabs, built from a server id (R2, R12).
 *
 * An id of the allowed characters (`A-Z`, `a-z`, `0-9`, `.`, `_`, `~`, `-`) gives
 * `/platform/data-planes/{id}`, plus the tab's segment for a tab other than the default. Any other
 * id gives {@link DATA_PLANES_ROUTE}: an empty id, a dot segment, the create segment `new` in any
 * letter case (`NEW`, `New`), or an id with a `/`, a space or another character. React Router
 * matches the static create route without regard to case, so a server string never resolves to
 * another screen. Older builders of the same path do not call it (R14). Pure.
 */
export function dataPlaneRoute(dpId: string, tab: DpTab = DP_TAB_DEFAULT): string {
  if (RESERVED_DP_IDS.has(dpId.toLowerCase()) || !SAFE_DP_ID.test(dpId)) return DATA_PLANES_ROUTE;
  return dpTabPath(encodeURIComponent(dpId), tab);
}

/**
 * The trailing path segment → the destination, or `null` for **"no such destination"**.
 *
 * ⛔ **`null` IS NOT `DP_TAB_DEFAULT`, AND THE DIFFERENCE IS THE WHOLE OF R9-15.** The retired
 * `parseDpTab` resolved an unrecognised `?tab=` *silently* to Overview, which was right for a query
 * param — a typo in a param is noise, and the screen it decorates still exists. A path segment is
 * different: `/platform/data-planes/:id/loggs` is a URL that does not exist, and rendering Overview
 * at it would mint a second, wrong URL for Overview and leave the rail unable to highlight anything.
 *
 * ⇒ The caller REDIRECTS to `dpTabPath(dpId, DP_TAB_DEFAULT)`, so the bad URL is *replaced* rather
 * than *served*. ⛔ Never let it fall through to `AppHost`'s `<Route path="*">`, which navigates to
 * the landing screen — a silent teleport to the Tenants list is exactly the failure this whole
 * conversion had to avoid re-creating somewhere new.
 *
 * ⚠️ `''` (the record's own URL) resolves to {@link DP_TAB_DEFAULT} rather than to `null`.
 */
export function parseDpTabSegment(raw: string): DpTab | null {
  if (raw === '') return DP_TAB_DEFAULT;
  // ⛔ Checked against the segment form, not the id: `dpTabSegment(DP_TAB_DEFAULT)` is `''`, so a
  // literal `overview` segment is NOT a destination — see `dpTabSegment`.
  return isDpTab(raw) && raw !== DP_TAB_DEFAULT ? raw : null;
}

/** The two segments inside `history`, which are the tabs ruling R-1 collapsed to create it. */
export const HISTORY_SEGMENTS = ['jobs', 'activity'] as const;

export type HistorySegment = (typeof HISTORY_SEGMENTS)[number];

/*
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * LEGACY URLs — the transition window (R9-16, opened 2026-08-07)
 * ────────────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⛔ **THE LEGACY QUERY KEY. It is no longer how a destination is chosen — it is only how an OLD
 * LINK is understood.**
 *
 * Docs, Slack messages and browser bookmarks carry `?tab=logs` and `?tab=diagnostics` today. A
 * silent landing on Overview would be *"a 404 dressed as a working page"*: the reader concludes the
 * thing they saved is gone, when it is one click away. ⇒ {@link legacyDpDestination} maps the value
 * to a destination and `DataPlanePane` issues a `<Navigate replace>` to its path.
 *
 * ⚠️ **The redirect PRESERVES the whole search string**, including `tab` itself — see
 * {@link historySegmentFromRetiredTab}, which still has to read it on the History route.
 */
export const DP_TAB_PARAM = 'tab';

/**
 * ⛔ **THE OTHER LEGACY KEY, AND IT IS EASY TO MISS BECAUSE THE RULING NAMED ONE DOOR.**
 *
 * Metrics shipped as `?screen=metrics` — a SEPARATE param from `?tab=`, on the recorded grounds that
 * `AppHost` had no grandchild route and a sixth strip tab would wrap. **Both grounds are gone** (the
 * splat route, and the rail), so ruling **D-1** makes Metrics a destination and the whole
 * `dp-screen.ts` module was deleted with it.
 *
 * ⚠️ The param outlives the module: the same bookmarks exist. It is read here, once, for the
 * redirect, and nowhere else.
 */
export const DP_SCREEN_PARAM = 'screen';

/** The one `?screen=` value that ever shipped. ⚠️ Any other value is junk and is ignored. */
const LEGACY_SCREEN_METRICS = 'metrics';

/**
 * ⭐ **RETIRED TAB IDS, ALIASED TO WHERE THEIR CONTENT ACTUALLY WENT.**
 *
 * `jobs` and `activity` were collapsed into `history` by ruling R-1. An unknown id is a mistake and
 * gets the default; a **retired** id is a fact we know, and we know exactly where it went. ⚠️ These
 * are different situations and collapsing them into one fallback is the same class of error as
 * rendering `errorstate` as `empty`.
 *
 * ⛔ **This map may only ever GROW.** Deleting an entry silently breaks every surviving old link, and
 * the breakage is invisible — the operator lands on a real screen and just doesn't find their thing.
 */
export const RETIRED_DP_TABS: Readonly<Record<HistorySegment, DpTab>> = {
  jobs: 'history',
  activity: 'history',
};

function isRetiredDpTab(value: string): value is HistorySegment {
  return (HISTORY_SEGMENTS as readonly string[]).includes(value);
}

/**
 * Which `history` segment a retired `?tab=` was asking for, or `null`.
 *
 * ⛔ **`jobs` / `activity` STAY A QUERY PARAM, AND THAT IS THE RULING, NOT AN OMISSION** (N-12):
 * **destinations are routes; facets of one destination are params.** That is the same line N-11
 * draws between a scoped rail group and an in-page control, applied one level down — and it is why
 * `SegmentedControl` survives while the tab strip does not.
 *
 * ⇒ ⚠️ **This is why the R9-16 redirect must preserve the search string rather than replace it.**
 * `?tab=jobs` → `/platform/data-planes/:id/history?tab=jobs`: the path carries the destination, the
 * surviving param carries WHICH HALF. Dropping it would land an old bookmark on the right screen
 * showing the wrong half — a subtler version of the defect the alias exists to fix, and the reader
 * has to work out that a control they have never seen before is why their content is missing.
 *
 * ⚠️ Returns `null` for a current destination id and for junk alike — both mean "no segment was
 * requested", so the History destination uses its own default. Deliberately not an error.
 */
export function historySegmentFromRetiredTab(raw: string | null): HistorySegment | null {
  if (raw === null) return null;
  return isRetiredDpTab(raw) ? raw : null;
}

/**
 * A legacy `?tab=` value → the destination it asked for.
 *
 * ⛔ **RETIRED IDS ARE CHECKED BEFORE THE DEFAULT, AND THE ORDER IS THE WHOLE POINT.** Reversing the
 * two lines makes the alias dead code and every surviving `?tab=jobs` link silently lands on
 * Overview — which is exactly the behaviour before R-1 and looks identical to working.
 *
 * ⚠️ Unlike {@link parseDpTabSegment} this DOES fall back to the default: an unrecognised value in a
 * legacy *param* is a typo in a decoration, and the record it decorates still exists. The strict
 * arm belongs to the path, which is the thing that has to be a real URL.
 */
export function parseDpTab(raw: string | null): DpTab {
  if (raw === null) return DP_TAB_DEFAULT;
  if (isDpTab(raw)) return raw;
  if (isRetiredDpTab(raw)) return RETIRED_DP_TABS[raw];
  return DP_TAB_DEFAULT;
}

/** Where a legacy link should land: an absolute path plus the search string that survives the trip. */
export interface DpLegacyRedirect {
  readonly pathname: string;
  /** `''` or `'?tab=jobs'`. ⚠️ Never a bare `?` — that is a different string for the same location. */
  readonly search: string;
}

/**
 * ⭐⭐ **THE WHOLE LEGACY-URL RULE, IN ONE PLACE** (R9-16, opened 2026-08-07). Given the search
 * params seen at a data plane's **own** URL, where an old link should be sent — or `null` if this was
 * not an old link and nothing should happen.
 *
 * ## ⛔ `?screen=` WINS OVER `?tab=`, AND THAT REPRODUCES THE RETIRED PRECEDENCE EXACTLY
 *
 * The two keys coexisted, and `?screen=metrics` deliberately PRESERVED `?tab=` so that leaving the
 * metrics screen put the operator back where they were. ⇒ **every surviving metrics bookmark has
 * BOTH keys in it**, and reading `tab` first would send all of them to the wrong destination. ⚠️ That
 * is the single highest-traffic legacy shape, not an edge case.
 *
 * ## ⛔⛔ THE SEARCH STRING IS STRIPPED, WITH ONE EXCEPTION — AND THE STRIP IS WHAT PREVENTS A LOOP
 *
 * The caller redirects only from the record's own URL (the empty trailing segment). So a redirect
 * that LANDS on the record's own URL and keeps its params redirects again, forever. `?tab=overview`
 * is exactly that shape, and it is a real link — the retired `dpTabSearch` emitted `?tab=overview`
 * for the default *"so Overview is linkable as an explicit choice"*, so those links exist.
 *
 * ⇒ Both legacy keys are dropped. **The one exception is a retired `?tab=jobs` / `?tab=activity`**,
 * which is not a destination request at all — it names WHICH HALF of History to show, is read by
 * {@link historySegmentFromRetiredTab} on the History route, and would be unrecoverable if dropped.
 * ⭐ That exception is the ruling in miniature: *destinations are routes; facets of one destination
 * are params.* It cannot loop, because its destination is `history`, never the root.
 *
 * ```
 *   ?tab=logs                  → /platform/data-planes/:id/logs
 *   ?tab=jobs                  → /platform/data-planes/:id/history?tab=jobs   ← the exception
 *   ?tab=overview              → /platform/data-planes/:id                    ← stripped, or it loops
 *   ?screen=metrics&tab=diag…  → /platform/data-planes/:id/metrics
 *   (no legacy key)            → null                                        ← not a redirect
 * ```
 *
 * ⚠️ Returning `null` when neither key is present is load-bearing: a redirect on every ordinary DP
 * visit would burn a history entry and fight the back button.
 */
export function legacyDpRedirect(dpId: string, params: URLSearchParams): DpLegacyRedirect | null {
  const screen = params.get(DP_SCREEN_PARAM);
  const rawTab = params.get(DP_TAB_PARAM);
  if (screen !== LEGACY_SCREEN_METRICS && rawTab === null) return null;

  const tab: DpTab = screen === LEGACY_SCREEN_METRICS ? 'metrics' : parseDpTab(rawTab);
  const segment = historySegmentFromRetiredTab(screen === LEGACY_SCREEN_METRICS ? null : rawTab);
  return {
    pathname: dpTabPath(dpId, tab),
    search: segment === null ? '' : `?${DP_TAB_PARAM}=${segment}`,
  };
}
